import { uniq, uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import {
  Checkbox,
  ContainerText,
  Divider,
  HelpText,
  HorizontalList,
  Tooltip,
} from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { useTheme } from 'styled-components';
import { FormState, GlobalFilterLocation } from 'src/types';
import {
  labelsForFullBoxSelect,
  stringArraysMatchAsMultisets,
} from 'src/utils/boxFilterFullSelection';
import { DividerContainer } from '../CustomTimeframeFilter/CustomTimeframeFilter.styles';
import { LocalFilterSectionTitle } from '../LocalFilterSectionTitle';
import { SelectAllButton } from './components/SelectAllButton';
import { ShowMoreOptions } from './components/ShowMoreOptions';
import {
  LocalFilterSectionStyled,
  SectionHeader,
  SectionHeaderTextContainer,
} from './LocalFilterSection.styles';

interface Props {
  title: string;
  selectedOptions: string[];
  onChange: (option: string) => void;
  isGroupedLocationFilter?: boolean;
  limit?: number;
  limitMessage?: string;
  hasBottomDivider?: boolean;
  initialOptionsDisplaying?: number;
  filtersIsOpen?: boolean;
  onSelectAll?: () => void;
  showNoneSelectedError?: boolean;
  'data-testid'?: string;
  tooltipText?: string;
  selectedGroupedLocationFilter?: GlobalFilterLocation;
  setSelectedGroupedLocationFilter?: (location: GlobalFilterLocation) => void;
  paddingBottom?: number;
}
interface BoxProps extends Props {
  type: 'box';
  options: { label: string; description?: string; disabled?: boolean }[];
}
interface CheckboxProps extends Props {
  type: 'checkbox';
  options: string[];
}

export enum SelectAllButtonState {
  SELECT = 'Select all',
  CLEAR = 'Clear all',
}

export const LocalFilterSection = ({
  title,
  type,
  options,
  selectedOptions,
  onChange,
  onSelectAll,
  initialOptionsDisplaying = 8,
  showNoneSelectedError = true,
  isGroupedLocationFilter,
  selectedGroupedLocationFilter,
  setSelectedGroupedLocationFilter,
  paddingBottom,
  ...props
}: BoxProps | CheckboxProps) => {
  const theme = useTheme();
  const [optionsDisplaying, setOptionsDisplaying] = useState(
    initialOptionsDisplaying,
  );

  const enabledOptionsCount =
    type === 'box'
      ? options.reduce(
          (acc, option) => (option.disabled ? acc : acc.add(option.label)),
          new Set<string>(),
        ).size
      : options.length;
  const labelsIfAllSelectedForBox =
    type === 'box' ? labelsForFullBoxSelect(options, props.limit) : [];
  const multiSetMatch = stringArraysMatchAsMultisets(
    selectedOptions,
    labelsIfAllSelectedForBox,
  );
  const selectAllCondition =
    type === 'box'
      ? labelsIfAllSelectedForBox.length > 0 && multiSetMatch
        ? SelectAllButtonState.CLEAR
        : SelectAllButtonState.SELECT
      : selectedOptions.length === (props.limit ?? enabledOptionsCount)
        ? SelectAllButtonState.CLEAR
        : SelectAllButtonState.SELECT;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [root, setRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setOptionsDisplaying(initialOptionsDisplaying);
  }, [initialOptionsDisplaying, props.filtersIsOpen]);

  return (
    <LocalFilterSectionStyled {...props}>
      <SectionHeader>
        <SectionHeaderTextContainer>
          <LocalFilterSectionTitle
            selectedOptions={selectedOptions}
            title={title}
            isGroupedLocationFilter={isGroupedLocationFilter}
            selectedGroupedLocationFilter={selectedGroupedLocationFilter}
            setSelectedGroupedLocationFilter={setSelectedGroupedLocationFilter}
            data-testid={props['data-testid']}
          />

          {props.tooltipText && (
            <StyledIcon
              className={'uil uil-info-circle'}
              onMouseOver={() => setTooltipVisible(true)}
              onMouseOut={() => setTooltipVisible(false)}
              ref={setRoot}
              color={
                tooltipVisible
                  ? theme.atlas.semantic.color.action.primary.default
                  : theme.atlas.semantic.color.type.default
              }
              size="20px"
              data-testid="department-information-icon"
            />
          )}
          {tooltipVisible && root && props.tooltipText && (
            <Tooltip
              text={props.tooltipText}
              variant="up"
              root={root}
              width="264px"
              data-testid="department-tooltip"
            />
          )}
        </SectionHeaderTextContainer>
        <SelectAllButton
          onSelectAll={onSelectAll}
          selectAllButtonCondition={selectAllCondition}
        />
      </SectionHeader>
      <HorizontalList padding={20} maxLength={4} paddingBottom={paddingBottom}>
        {type === 'box'
          ? uniqBy(options, (item) => item.label)
              .slice(0, optionsDisplaying)
              .map((o) => (
                <ContainerText
                  text={o.label}
                  description={o.description}
                  isSelected={!o.disabled && selectedOptions.includes(o.label)}
                  disabled={o.disabled}
                  onClick={o.disabled ? undefined : () => onChange(o.label)}
                  data-testid={`container-${o.label}`}
                  key={o.label + '-' + title}
                />
              ))
          : uniq(options)
              .slice(0, optionsDisplaying)
              .map((o) => (
                <Checkbox
                  label={o}
                  labelPosition="right"
                  checked={selectedOptions.includes(o)}
                  variant="default"
                  onChange={() => onChange(o)}
                  data-testid={`checkbox-${o}`}
                  key={o + '-' + title}
                />
              ))}
      </HorizontalList>
      <ShowMoreOptions
        options={options}
        optionsDisplaying={optionsDisplaying}
        initialOptionsDisplaying={initialOptionsDisplaying}
        limitMessage={props.limitMessage}
        setOptionsDisplaying={setOptionsDisplaying}
      />
      {showNoneSelectedError && selectedOptions.length === 0 && (
        <HelpText
          data-testid={`${props['data-testid']}-error`}
          state={FormState.ERROR}
          helpText={`You must select at least one ${title.toLowerCase()}`}
        />
      )}
      {props.hasBottomDivider && (
        <DividerContainer
          hasErrorMessage={
            showNoneSelectedError && selectedOptions.length === 0
          }
        >
          <Divider variant="horizontal" />
        </DividerContainer>
      )}
    </LocalFilterSectionStyled>
  );
};
